import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import { getTeamLogoUrl } from '../../utils/formatters';
import { useAuth } from '../../contexts/AuthContext';
import RegionBadge from './RegionBadge';
import LeaguesTabs from './LeaguesTabs';
import './Leagues.css';

/**
 * Mode filter pills for the team directory. Only team-based modes have
 * persistent rosters, so duel is intentionally absent. Only the "all" label is
 * translated; the technical mode pills (2v2/5v5) are literal.
 */
const TEAM_MODE_FILTERS = [
  { key: '', labelKey: 'matches.all' },
  { key: 'wingman', label: '2v2' },
  { key: 'competitive', label: '5v5' },
];

/**
 * Public directory of persistent league teams as a dense, scannable list: one
 * compact row per team (logo, tag, name, region, mode, roster size). Clicking a
 * row opens the team detail page with the full roster.
 */
export default function LeagueTeams() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isLoggedIn } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('');

  useEffect(() => {
    setLoading(true);
    const url = mode
      ? `${API_URL}/api/leagues/teams?game_mode=${mode}`
      : `${API_URL}/api/leagues/teams`;
    fetch(url)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setTeams(Array.isArray(data) ? data : []))
      .catch(() => setTeams([]))
      .finally(() => setLoading(false));
  }, [mode]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loader" />
      </div>
    );
  }

  return (
    <div className="league-page">
      <div className="league-page-header">
        <div className="league-detail-titles">
          <h1 className="league-page-title">{t('createMatch.teams')}</h1>
        </div>
        <div className="league-header-actions">
          <div className="league-filters">
            {TEAM_MODE_FILTERS.map((f) => (
              <button
                key={f.key || 'all'}
                onClick={() => setMode(f.key)}
                className={`league-filter-btn ${mode === f.key ? 'active' : ''}`}
              >
                {f.labelKey ? t(f.labelKey) : f.label}
              </button>
            ))}
          </div>
          {isLoggedIn && (
            <button className="form-submit form-submit-sm" onClick={() => navigate('/leagues/teams/new')}>
              + {t('titles.createTeam')}
            </button>
          )}
        </div>
      </div>

      <LeaguesTabs />

      {teams.length === 0 ? (
        <div className="league-empty">{t('leagues.noTeams')}</div>
      ) : (
        <div className="team-rows">
          {teams.map((team) => (
            <button
              key={team.id}
              className="team-row"
              onClick={() => navigate(`/leagues/teams/${team.id}`)}
            >
              {getTeamLogoUrl(team.logo_url) ? (
                <img className="team-logo" src={getTeamLogoUrl(team.logo_url)} alt="" />
              ) : (
                <span className="team-logo team-logo-blank">{(team.tag || team.name || '?')[0]}</span>
              )}
              {team.tag && <span className="team-tag" title={t('leagues.teamTagTitle')}>{team.tag}</span>}
              <span className="team-row-name">{team.name}</span>
              <RegionBadge region={team.region} />
              <span className="league-mode-badge">{team.game_mode_display}</span>
              <span className="team-row-count">{t('leagues.playerCount', { count: team.member_count })}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
